import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/app/Common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { fmtDate } from "../../lib/utils";
import { AlertOctagon, PlusCircle, Trash2, Filter, ExternalLink } from "lucide-react";

const SEVERITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["Open", "Under Review", "Corrective Action", "Closed"];

const SEVERITY_STYLE = {
  Low:      "bg-muted text-muted-foreground border-border",
  Medium:   "bg-[hsl(var(--status-hold))/0.1] text-[hsl(var(--status-hold))] border-[hsl(var(--status-hold))/0.4]",
  High:     "bg-primary/10 text-primary border-primary/40",
  Critical: "bg-destructive/10 text-destructive border-destructive/40",
};
const STATUS_STYLE = {
  Open:                 "bg-destructive/10 text-destructive border-destructive/40",
  "Under Review":       "bg-blue-500/10 text-blue-500 border-blue-500/40",
  "Corrective Action":  "bg-[hsl(var(--status-hold))/0.1] text-[hsl(var(--status-hold))] border-[hsl(var(--status-hold))/0.4]",
  Closed:               "bg-[hsl(var(--status-pass))/0.1] text-[hsl(var(--status-pass))] border-[hsl(var(--status-pass))/0.4]",
};

const emptyForm = {
  title: "", description: "", severity: "Medium",
  project_id: "", assigned_to: "", due_date: "",
};

export default function AdminNCRs() {
  const [ncrs, setNcrs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const params = statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "";
    const { data } = await api.get(`/ncrs${params}`);
    setNcrs(data);
  }, [statusFilter]);

  useEffect(() => { 
    load(); 
  }, [load]);

  useEffect(() => {
    api.get("/jobsites").then(({ data }) => setProjects(data));
    api.get("/company/users").then(({ data }) => setUsers(data.filter((u) => u.status === "active")));
  }, []);

  const create = async () => {
    try {
      const payload = { ...form };
      if (!payload.project_id) delete payload.project_id;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.due_date) delete payload.due_date;
      const { data } = await api.post("/ncrs", payload);
      toast.success(`NCR ${data.number} raised`);
      setOpenNew(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete NCR?")) return;
    await api.delete(`/ncrs/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        title="Non-Conformance Reports"
        subtitle="Track deviations from spec, assign corrective actions, and close the loop from inspection failures."
        action={
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="new-ncr-btn">
                <PlusCircle className="h-4 w-4 mr-1.5" /> Raise NCR
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle>Raise NCR</DialogTitle>
                <DialogDescription>Report a non-conformance and optionally link it to a project or inspection.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="overline mb-1 block">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm font-mono" data-testid="ncr-title-input" />
                </div>
                <div>
                  <Label className="overline mb-1 block">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm font-mono min-h-[80px]" data-testid="ncr-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline mb-1 block">Severity</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="ncr-severity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline mb-1 block">Due date (optional)</Label>
                    <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm font-mono" data-testid="ncr-due-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline mb-1 block">Project (optional)</Label>
                    <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="ncr-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline mb-1 block">Assign to (optional)</Label>
                    <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="ncr-assignee-select"><SelectValue placeholder="Nobody" /></SelectTrigger>
                      <SelectContent>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={!form.title} className="rounded-sm bg-primary" data-testid="ncr-submit-btn">Raise NCR</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-2 mb-4 border border-border rounded-sm p-2 bg-card">
        <Filter className="h-4 w-4 text-muted-foreground ml-1" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52 rounded-sm h-9" data-testid="ncr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs font-mono text-muted-foreground pr-2">{ncrs.length} NCRs</div>
      </div>

      {ncrs.length === 0 ? (
        <EmptyState title="No NCRs" body="Every deviation lives here. Raise one from an inspection failure or manually." />
      ) : (
        <div className="border border-border rounded-sm overflow-hidden" data-testid="ncrs-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 overline text-left">
              <tr>
                <Th>NCR #</Th><Th>Title</Th><Th>Severity</Th><Th>Status</Th>
                <Th>Project</Th><Th>Raised by</Th><Th>Due</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {ncrs.map((n) => (
                <tr key={n.id} className="border-t border-border hover:bg-accent/30 cursor-pointer" onClick={() => setSelected(n)} data-testid={`ncr-row-${n.id}`}>
                  <Td mono>
                    <AlertOctagon className="inline h-4 w-4 mr-1.5 text-destructive" />
                    {n.number}
                  </Td>
                  <Td>{n.title}</Td>
                  <Td>
                    <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${SEVERITY_STYLE[n.severity]}`}>
                      {n.severity}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${STATUS_STYLE[n.status]}`}>
                      {n.status}
                    </Badge>
                  </Td>
                  <Td mono>{n.project_name || "—"}</Td>
                  <Td>{n.raised_by}</Td>
                  <Td mono>{n.due_date ? fmtDate(n.due_date) : "—"}</Td>
                  <Td>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(n.id); }} data-testid={`ncr-del-${n.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <NCRDetail
          ncr={selected}
          users={users}
          onClose={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function NCRDetail({ ncr, users, onClose }) {
  const [n, setN] = useState(ncr);
  const [busy, setBusy] = useState(false);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/ncrs/${n.id}`, payload);
      setN(data);
      toast.success("Updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-destructive" /> {n.number} — {n.title}
          </DialogTitle>
          <DialogDescription>
            Raised by {n.raised_by} on {fmtDate(n.raised_at)}
            {n.inspection_id && <span> · linked to inspection <span className="font-mono">{n.inspection_id.slice(0, 8)}…</span></span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest ${SEVERITY_STYLE[n.severity]}`} data-testid="ncr-detail-severity">
              {n.severity}
            </Badge>
            <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest ${STATUS_STYLE[n.status]}`} data-testid="ncr-detail-status">
              {n.status}
            </Badge>
            {n.project_name && <Badge variant="outline" className="rounded-sm font-mono uppercase tracking-widest text-[10px]">{n.project_name}</Badge>}
          </div>

          <div>
            <div className="overline mb-1">Description</div>
            <div className="text-sm whitespace-pre-wrap">{n.description || "—"}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline mb-1 block">Status</Label>
              <Select value={n.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="ncr-detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline mb-1 block">Assigned to</Label>
              <Select value={n.assigned_to || ""} onValueChange={(v) => patch({ assigned_to: v })}>
                <SelectTrigger className="rounded-sm" data-testid="ncr-detail-assignee-select"><SelectValue placeholder="Nobody" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline mb-1 block">Root cause</Label>
            <Textarea
              defaultValue={n.root_cause}
              onBlur={(e) => patch({ root_cause: e.target.value })}
              className="rounded-sm font-mono text-sm"
              placeholder="Analysis of why this happened"
              data-testid="ncr-root-cause-input"
              disabled={busy}
            />
          </div>
          <div>
            <Label className="overline mb-1 block">Corrective action</Label>
            <Textarea
              defaultValue={n.corrective_action}
              onBlur={(e) => patch({ corrective_action: e.target.value })}
              className="rounded-sm font-mono text-sm"
              placeholder="Steps taken / to be taken"
              data-testid="ncr-corrective-input"
              disabled={busy}
            />
          </div>

          {n.closed_at && (
            <div className="text-xs font-mono text-[hsl(var(--status-pass))] border-t border-border pt-3">
              Closed by {n.closed_by} on {fmtDate(n.closed_at)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 font-mono text-[10px] tracking-widest">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-4 py-3 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>;